// Google Drive item types
export type DriveItem = {
  id: string;
  name: string;
  mimeType?: string;
};

// User profile type
export interface UserProfile {
  authenticated: boolean;
  tokenPresent?: boolean;
  userId?: string;
  tokenExpiry?: number;
  email?: string;
  name?: string;
}

// Task status enum
export enum TaskStatus {
  Created = 'created',
  InProgress = 'in_progress',
  Completed = 'completed',
  Failed = 'failed'
}

// Task type enum (complete pipeline stages)
export enum TaskType {
  Genesis = 'genesis',           // File Download
  Process = 'process',           // Metadata Extraction
  ChunkPlan = 'chunk_plan',      // Chunking Plan
  ChunkExecute = 'chunk_execute', // Chunking Execution
  Upload = 'upload',             // Gemini Upload
  Extract = 'extract',           // AI Extract
  Embedding = 'embedding',       // Embedding Creation
  DbLoad = 'db_load'             // Vector DB Loading
}

// Pipeline stages with descriptions for the UI
export const PIPELINE_STAGES: Record<string, { title: string; description: string }> = {
  'genesis': { 
    title: 'File Download', 
    description: 'Downloading files from Google Drive'
  },
  'process': { 
    title: 'Metadata Extraction', 
    description: 'Analyzing files to extract basic metadata'
  },
  'chunk_plan': { 
    title: 'Chunking Plan', 
    description: 'Planning how to break down content into smaller units'
  },
  'chunk_execute': { 
    title: 'Chunking Execution', 
    description: 'Splitting content into semantic units'
  },
  'upload': { 
    title: 'Gemini Upload', 
    description: 'Uploading processed content to Google Gemini AI'
  },
  'extract': { 
    title: 'AI Extraction', 
    description: 'Using AI to extract key information'
  },
  'embedding': { 
    title: 'Embedding Creation', 
    description: 'Generating vector embeddings for search'
  },
  'db_load': { 
    title: 'Vector DB Loading', 
    description: 'Storing embeddings in vector database'
  }
};

// Task interface based on actual SSE data
export interface Task {
  task_id: string;
  genesis_task_id: string;
  task_type: string;
  input_data_file_path: string;
  output_data_file_path: string | null;
  status: string;
  is_completed: boolean;
  is_inflight_at: string | null;
  created_at: string;
  updated_at: string;
  // Progress metrics
  total_items?: number;
  started_items?: number;
  succeeded_items?: number;
  failed_items?: number;
}

// New task status interface for the updated API
export interface TaskStatusResponse {
  task_id: string;
  task_hint: string | null;
  created_at: string;
  updated_at: string;
  phases: {
    x: PhaseStatus; // File processing (downloading, chunking)
    y: PhaseStatus; // Chunk processing (Gemini AI extraction)
    z: PhaseStatus; // Extraction processing (embeddings)
  };
}

// Phase status interface
export interface PhaseStatus {
  total: number;
  status: {
    pending: number;
    in_flight: number;
    completed: number;
    failed: number;
    perm_failed: number;
  };
  attempts: {
    [key: string]: number; // Keys are attempt numbers as strings: "0", "1", etc.
  };
}

// Search result item interface
export interface SearchResult {
  extraction_text: string;
  genesis_task_id: string;
  similarity_score: string;
  google_drive_url: string;
  chunk_index: number;
  // Document-specific fields
  start_page?: number;
  end_page?: number;
  // Audio/video-specific fields
  start_time?: number;
  end_time?: number;
}

// Search response interface
export interface SearchResponse {
  query: string;
  timestamp: string;
  results_count: number;
  results: SearchResult[];
}

// API Error type
export interface ApiError {
  statusCode: number;
  message: string;
  code?: string;
} 